import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { selectStandardProps } from "@/utils/componentUtils";
import { cssVariables } from "@/utils/utils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { WarningTwoTone } from "@ant-design/icons";
import { Checkbox, Empty, Flex, Form, Input, Popconfirm, Select, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";
import { useDeleteCalcSettingsAttachmentConf } from "../../../../../../common/api/queries";
import HiddenInput from "../../../../../../common/components/form/components/HiddenInput";
import ActionTextIcon from "../../../../../../common/components/icons/ActionTextIcon";
import AntIcon from "../../../../../../common/components/icons/AntIcon";
import PopconfirmDeleteIcon from "../../../../../../common/components/icons/PopconfirmDeleteIcon";
import { rowGutter } from "../../../../../../common/constants";
import { ContractAttachmentType } from "../../../../../contract/enums";
import { AttachmentConfType } from "../../../enums";
import { ADMIN_CALC_ROUTE_PATHS } from "../../../paths";
import { CalcSettings, UpdateCalcSettings } from "../../../types";
import { CALC_ATTACHMENT_FILTER } from "../../../utils";
import styles from "./AttachmentsConfList.module.scss";
import type { CustomAttachmentConf } from "./CalcSettingsAttachmentConfsPart";

type AttachmentsConfDetailProps = {
  attachments: CustomAttachmentConf[];
  calcSettings: CalcSettings;
};

export const AttachmentsConfList = ({ attachments, calcSettings }: AttachmentsConfDetailProps) => {
  const { t } = useTranslation();

  const deleteCalcSettingsAttachmentConf = useDeleteCalcSettingsAttachmentConf();

  const form = Form.useFormInstance<UpdateCalcSettings>();
  const attachmentConfs = Form.useWatch("attachmentConfs", form);

  return (
    <div className={styles.wrapper}>
      {attachments.map((conf) => (
        <div key={conf.key} className={styles.box}>
          <HiddenInput name={["attachmentConfs", conf.key, "id"]} />
          <HiddenInput name={["attachmentConfs", conf.key, "optimisticLockVersion"]} />

          <div style={{ marginBottom: 4 }}>
            <div className={styles.header}>
              <div>
                <span>{t("calc.settings.attrs.attachmentConfs.originalFilename")}: </span>
                <ActionTextIcon
                  path={generatePath(ADMIN_CALC_ROUTE_PATHS.attachmentConf.to, {
                    id1: calcSettings.id,
                    id2: conf.id,
                  })}
                  target="_blank"
                  icon="export"
                  color="green"
                  size="large"
                  text={conf.originalFilename}
                />

                {CALC_ATTACHMENT_FILTER.some(
                  (filter) =>
                    (conf.type === AttachmentConfType.STATIC || conf.type === AttachmentConfType.DYNAMIC) &&
                    conf.filename === filter.fileName &&
                    calcSettings.institution.institutionEnum === filter.institutionEnum &&
                    filter.calcType.includes(calcSettings.type),
                ) ? (
                  <Tooltip title={t("calc.helpers.hideSpecificFile")}>
                    <WarningTwoTone
                      className="margin-left-tiny"
                      twoToneColor={cssVariables.colorYellow}
                      style={{ fontSize: 18 }}
                    />
                  </Tooltip>
                ) : undefined}
              </div>

              <div className={styles.deleteWrapper}>
                <Popconfirm
                  title={t("calc.settings.titles.deleteAttachmentConfirm")}
                  icon={<PopconfirmDeleteIcon />}
                  okText={t("common.yes")}
                  cancelText={t("common.no")}
                  okType="danger"
                  onConfirm={() => deleteCalcSettingsAttachmentConf.mutate({ id1: calcSettings.id, id2: conf.id })}
                >
                  <AntIcon type="delete" color="red" size="huge" />
                </Popconfirm>
              </div>
            </div>

            <ItemCreatedUpdatedInfoView item={conf} />
          </div>

          <Flex gap={rowGutter} align="flex-end">
            <Form.Item
              style={{ width: 300 }}
              name={["attachmentConfs", conf.key, "filename"]}
              label={t("calc.settings.attrs.attachmentConfs.filename")}
              rules={[validations.notBlank, validations.pattern(regexPatterns.filenameRegex)]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              style={{ width: 100 }}
              name={["attachmentConfs", conf.key, "contractAttachmentType"]}
              dependencies={[["attachmentConfs", conf.key, "shouldAttachToContract"]]}
              label={t("contract.enums.contractAttachmentType._label")}
              rules={[attachmentConfs?.[conf.key]?.shouldAttachToContract ? validations.notNull : validations.none]}
            >
              <Select
                {...selectStandardProps}
                allowClear
                options={Object.values(ContractAttachmentType).map((type) => ({
                  value: type,
                  label: t(`contract.enums.contractAttachmentType.${type}`),
                }))}
              />
            </Form.Item>

            <Form.Item
              name={["attachmentConfs", conf.key, "shouldAttachToEmail"]}
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
            >
              <Checkbox>{t("calc.settings.attrs.attachmentConfs.shouldAttachToEmail")}</Checkbox>
            </Form.Item>

            <Form.Item
              style={{ display: "inline-block" }}
              name={["attachmentConfs", conf.key, "shouldAttachToContract"]}
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
            >
              <Checkbox>{t("calc.settings.attrs.attachmentConfs.shouldAttachToContract")}</Checkbox>
            </Form.Item>

            <Form.Item
              style={{ display: "inline-block" }}
              name={["attachmentConfs", conf.key, "shouldEncrypt"]}
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
            >
              <Checkbox>{t("calc.settings.attrs.attachmentConfs.shouldEncrypt")}</Checkbox>
            </Form.Item>

            {conf.type !== AttachmentConfType.PROVIDED ? (
              <Form.Item
                style={{ display: "inline-block" }}
                name={["attachmentConfs", conf.key, "shouldAttachToCalc"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox>{t("calc.settings.attrs.attachmentConfs.shouldAttachToCalc")}</Checkbox>
              </Form.Item>
            ) : undefined}
          </Flex>
        </div>
      ))}

      {attachments.length === 0 ? <Empty className={styles.emptyBox} description={t("common.noData")} /> : undefined}
    </div>
  );
};
